import React from 'react'

/**
 * Diamond-staircase bar pattern + dark editorial overlays for cover slide backgrounds.
 * Figma source: node 1934:7458 (PATTERN + EFFECT).
 *
 * 11 horizontal bars form a symmetric diamond shape on the right side,
 * each filled with a left-to-right accent gradient and a fractal noise
 * texture. The overlays keep the text side dark while the right side carries
 * a visible, structured brand field instead of a brand-to-black wash.
 *
 * Color inherits from `--ds-cover-accent-*`, allowing brand-level override
 * through ThemeProvider.
 */

const VB_W = 1399
const VB_H = 790

interface Bar {
  x: number
  y: number
  w: number
  h: number
}

const BARS: Array<Bar> = [
  { x: 463,      y: 1,       w: 936,     h: 71.255 },
  { x: 586.919,  y: 72.255,  w: 812.081, h: 72.094 },
  { x: 703.403,  y: 144.349, w: 695.598, h: 71.255 },
  { x: 803.364,  y: 215.604, w: 595.636, h: 72.094 },
  { x: 915.717,  y: 287.698, w: 483.283, h: 71.255 },
  { x: 1023.94,  y: 358.953, w: 375.061, h: 72.094 },
  { x: 915.717,  y: 431.047, w: 483.283, h: 71.255 },
  { x: 803.364,  y: 502.302, w: 595.636, h: 72.094 },
  { x: 703.403,  y: 574.396, w: 695.598, h: 71.255 },
  { x: 586.919,  y: 645.651, w: 812.081, h: 72.094 },
  { x: 463,      y: 717.745, w: 936,     h: 71.255 },
]

const NOISE_TABLE =
  '1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 ' +
  '1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 ' +
  '1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 ' +
  '0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 '

export function CoverBackground(props: { idPrefix?: string }) {
  // A deck render mounts every slide at once, so a second copy needs its own
  // filter ids or the pattern paints against the cover's filters.
  const idPrefix = props.idPrefix ?? 'cover'
  return (
    <>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          background:
            'linear-gradient(180deg, var(--ds-cover-graphite-bg-soft) 0%, var(--ds-cover-graphite-bg) 62%, var(--ds-cover-graphite-bg-deep) 100%)',
        }}
      >
        <div
          data-testid="cover-brand-field"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, transparent 0%, transparent 42%, rgba(var(--ds-cover-brand-field-rgb), 0.2) 62%, rgba(var(--ds-cover-brand-field-rgb), var(--ds-cover-brand-field-alpha)) 100%)',
          }}
        />

        <div
          data-testid="cover-accent-glow"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 86% 48%, rgba(var(--ds-cover-brand-field-rgb), var(--ds-cover-accent-alpha)) 0%, rgba(var(--ds-cover-brand-field-rgb), calc(var(--ds-cover-accent-alpha) * 0.62)) 30%, transparent 56%)',
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 'var(--ds-cover-pattern-opacity)',
          }}
        >
          <svg
            data-testid="cover-background-pattern"
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              color: 'rgb(var(--ds-cover-accent-rgb))',
            }}
          >
            <defs>
              <filter
                id={`${idPrefix}-noise`}
                x="0"
                y="0"
                width={VB_W}
                height={VB_H}
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="bgFix" />
                <feBlend mode="normal" in="SourceGraphic" in2="bgFix" result="shape" />
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="2 2"
                  stitchTiles="stitch"
                  numOctaves={3}
                  seed={3748}
                  result="noise"
                />
                <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
                <feComponentTransfer in="alphaNoise" result="mask">
                  <feFuncA type="discrete" tableValues={NOISE_TABLE} />
                </feComponentTransfer>
                <feComposite operator="in" in2="shape" in="mask" result="clipped" />
                <feFlood floodColor="rgba(0, 0, 0, 0.25)" result="tint" />
                <feComposite operator="in" in2="clipped" in="tint" result="tinted" />
                <feMerge>
                  <feMergeNode in="shape" />
                  <feMergeNode in="tinted" />
                </feMerge>
              </filter>

              {BARS.map((bar, i) => (
                <linearGradient
                  key={i}
                  id={`${idPrefix}-bar-${i}`}
                  x1={bar.x}
                  y1={bar.y + bar.h / 2}
                  x2={VB_W}
                  y2={bar.y + bar.h / 2}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="currentColor" stopOpacity={0} />
                  <stop offset={1} stopColor="currentColor" />
                </linearGradient>
              ))}
            </defs>

            <g filter={`url(#${idPrefix}-noise)`}>
              {BARS.map((bar, i) => (
                <rect
                  key={i}
                  x={bar.x}
                  y={bar.y}
                  width={bar.w}
                  height={bar.h}
                  fill={`url(#${idPrefix}-bar-${i})`}
                />
              ))}
            </g>
          </svg>
        </div>

        <div
          data-testid="cover-text-scrim"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(90deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.78) 37%, rgba(0, 0, 0, 0.34) 58%, rgba(0, 0, 0, 0.04) 100%)',
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0.16) 0%, transparent 36%, rgba(0, 0, 0, 0.2) 100%)',
          }}
        />
      </div>
    </>
  )
}
